/**
 * i18n: Mongolian, English, Russian.
 *
 * The original carried all three. Each Power Apps label control held Mongolian
 * text with an `RU` property alongside, switched by the `SelectedLanguage`
 * global (set to "EN" in App.OnStart). Mongolian is the default here because
 * that is what the document templates are written in.
 *
 * ONE FILE PER AREA, in ./areas, so features adding keys do not collide.
 * Adding a NEW area means a new file and one line in `resources` below. Adding
 * keys to an existing one means editing only that file.
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import { accessMatrix } from './areas/accessMatrix'
import { accounts } from './areas/accounts'
import { audit } from './areas/audit'
import { automation } from './areas/automation'
import { banks } from './areas/banks'
import { calendar } from './areas/calendar'
import { company } from './areas/company'
import { contractorLog } from './areas/contractorLog'
import { equipmentRequests } from './areas/equipmentRequests'
import { hotdesk } from './areas/hotdesk'
import { homewall } from './areas/homewall'
import { hr } from './areas/hr'
import { inventory } from './areas/inventory'
import { oreas } from './areas/oreas'
import { me } from './areas/me'
import { organization } from './areas/organization'
import { operations } from './areas/operations'
import { otp } from './areas/otp'
import { password } from './areas/password'
import { publicView } from './areas/publicView'
import { payroll } from './areas/payroll'
import { payrollWarnings } from './areas/payrollWarnings'
import { resignationPayout } from './areas/resignationPayout'
import { oneOff } from './areas/oneOff'
import { roster } from './areas/roster'
import { rosterCity } from './areas/rosterCity'
import { rosterRefusal } from './areas/rosterRefusal'
import { sales } from './areas/sales'
import { shell } from './areas/shell'
import { time } from './areas/time'
import { leaveTeam } from './areas/leaveTeam'
import { resetFix } from './areas/resetFix'
import { timeApprove } from './areas/timeApprove'
import { uiKit } from './areas/uiKit'

const AREAS = [
  shell,
  payroll,
  time,
  timeApprove,
  leaveTeam,
  resetFix,
  roster,
  sales,
  hr,
  hotdesk,
  audit,
  automation,
  inventory,
  oreas,
  organization,
  otp,
  password,
  publicView,
  banks,
  resignationPayout,
  oneOff,
  company,
  me,
  homewall,
  calendar,
  operations,
  uiKit,
  accessMatrix,
  contractorLog,
  accounts,
  equipmentRequests,
  rosterCity,
  rosterRefusal,
  payrollWarnings,
]

/** Every area's keys for one language, merged into the flat shape i18next
 *  expects. The areas are disjoint by construction — each top-level key lives
 *  in exactly one file — so a plain merge is enough and a collision would be a
 *  mistake rather than an override. */
function merge(language: 'mn' | 'en' | 'ru') {
  return Object.assign({}, ...AREAS.map((area) => area[language]))
}

export const resources = {
  mn: { translation: merge('mn') },
  en: { translation: merge('en') },
  ru: { translation: merge('ru') },
}

function storedLanguage(): string {
  try {
    return localStorage.getItem('trg_lang') ?? 'en'
  } catch {
    // A third-party iframe can have storage blocked.
    return 'en'
  }
}

void i18n.use(initReactI18next).init({
  resources,
  lng: storedLanguage(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
})

// `index.html`'s static `lang` attribute only matches whichever
// language boots first. Keep it in sync both for a returning user (no
// `languageChanged` event fires on init) and for a live switch.
function syncDocumentLang(): void {
  if (typeof document === 'undefined') return
  document.documentElement.lang = i18n.resolvedLanguage ?? i18n.language
}
syncDocumentLang()
i18n.on('languageChanged', syncDocumentLang)

export function setLanguage(language: string): void {
  localStorage.setItem('trg_lang', language)
  void i18n.changeLanguage(language)
}

export default i18n
